import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { statusService } from '../services/api';
import { Card, Input, Button, PageHeader, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { usePageTitle } from '../context/PageTitleContext';

const CreateEditStatus = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3498db');
  const [priority, setPriority] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');
  const [colorError, setColorError] = useState('');
  
  const isEditMode = !!id && location.pathname.includes('/edit/');
  const isViewMode = !!id && location.pathname.includes('/view/');

  useEffect(() => {
    setPageTitle(isViewMode ? 'View Status' : isEditMode ? 'Edit Status' : 'Add Status');
  }, [isEditMode, isViewMode, setPageTitle]);

  useEffect(() => {
    if (isEditMode || isViewMode) {
      // If editing or viewing, fetch the status data
      fetchStatusData();
    } else if (location.state?.status) {
      // If creating with prefilled data from state
      const status = location.state.status;
      setName(status.name || '');
      setColor(status.color || '#3498db');
      setPriority(status.priority || 0);
    }
  }, [id, isEditMode, isViewMode, location.state]);

  const fetchStatusData = async () => {
    try {
      setLoading(true);
      const response = await statusService.getAll({ page: 1, limit: 1000 });
      const status = response.data.data.find(stat => stat.id === parseInt(id));
      
      if (status) {
        setName(status.name);
        setColor(status.color || '#3498db');
        setPriority(status.priority || 0);
      } else {
        toast.error('Status not found');
        navigate('/admin/statuses');
      }
    } catch (err) {
      toast.error('Failed to fetch status details');
      console.error(err);
      navigate('/admin/statuses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setNameError('');
    setColorError('');
    setError('');
    
    // Validate
    if (!name.trim()) {
      setNameError('Status name is required');
      return;
    }

    if (!color) {
      setColorError('Color is required');
      return;
    }

    try {
      setLoading(true);
      
      if (isEditMode) {
        await statusService.update(id, { 
          name, 
          color, 
          priority: parseInt(priority) 
        });
        toast.success('Status updated successfully');
      } else {
        await statusService.create({ 
          name, 
          color, 
          priority: parseInt(priority) 
        });
        toast.success('Status created successfully');
      }
      
      navigate('/admin/statuses');
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} status`;
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/statuses');
  };

  if (loading && (isEditMode || isViewMode)) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading status details...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Card className="w-full">
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate className="w-full">
          {/* Breadcrumbs Row */}
          <Breadcrumbs
            items={[
              { label: 'Statuses', to: '/admin/statuses' },
              { label: isViewMode ? 'View Status' : isEditMode ? 'Edit Status' : 'Add Status' }
            ]}
            className="mb-6"
          />
          {/* Inputs Row: Name, Color and Priority */}
          <div className="flex flex-col md:flex-row gap-4 mb-8 w-full">
            {/* Status Name */}
            <div className="flex-1">
              <Input
                label="Status Name"
                name="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError('');
                }}
                placeholder="Enter status name"
                required
                error={nameError}
                disabled={loading || isViewMode}
                readOnly={isViewMode}
              />
            </div>
          </div>

          {/* Second Row: Color and Priority */}
          <div className="flex flex-col md:flex-row gap-4 mb-8 w-full">
            {/* Color Picker */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color <span className="text-red-500">*</span>
              </label>
              <input
                type="color"
                name="color"
                value={color}
                onChange={(e) => {
                  setColor(e.target.value);
                  if (colorError) setColorError('');
                }}
                className="w-full h-10 border border-gray-300 rounded-md cursor-pointer"
                disabled={loading || isViewMode}
                readOnly={isViewMode}
              />
              {colorError && (
                <p className="mt-1 text-sm text-red-600">{colorError}</p>
              )}
            </div>
            {/* Priority */}
            <div className="flex-1">
              <Input
                label="Priority"
                name="priority"
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                placeholder="0"
                min="0"
                helperText="Lower number = higher priority"
                disabled={loading || isViewMode}
                readOnly={isViewMode}
              />
            </div>
          </div>

          {/* Actions Row: Buttons at far right */}
          <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              className="border-primary-600 text-primary-600 hover:bg-primary-50"
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditMode ? 'Update Status' : '+ Add Status'}
              </Button>
            )}
            {isViewMode && (
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate(`/admin/statuses/edit/${id}`)}
              >
                Edit Status
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateEditStatus;
